import { Button, FlexCenter } from "@9amhealth/shared";
import { useBloc as useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import clsx from "clsx";
import type { FC } from "react";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import { useBrowser } from "src/hybrid/components/Browser";
import createPath from "src/lib/createPath";
import { isHybridApp } from "src/lib/platform";
import translate from "src/lib/translate";
import AppAccessCubit, {
  AppAccessStatus
} from "src/state/AppAccessCubit/AppAccessCubit";
import useUserAuthStage from "src/state/hooks/useUserAuthStage";
import { TrackEvent } from "src/state/Track/TrackCubit";
import AuthenticationBloc from "src/state/UserCubit/AuthenticationBloc";
import { tracker } from "src/state/state";
import Translate from "src/ui/components/Translate/Translate";
import WithBackground from "../../WithBackground/WithBackground";

const StyledOverlay = styled.div`
  label: UnfinishedSignupOverlay;
  position: fixed;
  z-index: 999000;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition-delay: 0.6s;

  nine-bg {
    position: absolute;
    height: 100%;
  }

  &.show {
    pointer-events: all;
    transition-delay: 0;
    opacity: 1;
  }
`;

const HeadingSection = styled.section`
  max-width: ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px;
  margin: 0 auto;
  padding: 0 var(--space-md);
  text-align: center;
`;

const UnfinishedSignupOverlay: FC = () => {
  const [{ status, funnelRedirectPath, funnelKey }] =
    useBlocNext(AppAccessCubit);
  const [, { logout }] = useBlocNext(AuthenticationBloc);
  const { authStage } = useUserAuthStage();
  const autoOpenEnabled = !isHybridApp();
  const navigate = useNavigate();
  const [browser] = useBrowser();

  // only show/redirect if user is fully authenticated (if login overlay would not show)
  const isFullyAuthenticated = authStage === "fullAuth";
  const show = status === AppAccessStatus.NEEDS_FUNNEL && isFullyAuthenticated;

  const renderSignupButtonText = useMemo(() => {
    return isHybridApp()
      ? translate("continue.signup.browser")
      : translate("continue.signup");
  }, []);

  const handleContinueSignup = () => {
    if (!funnelRedirectPath) return;
    if (isHybridApp()) {
      void browser.open(createPath(funnelRedirectPath));
    } else {
      navigate(createPath(funnelRedirectPath));
    }
  };

  const handleLogout = () => {
    void logout();
  };

  useEffect(() => {
    if (show && autoOpenEnabled && funnelRedirectPath) {
      handleContinueSignup();
      tracker.track(TrackEvent.ShowBlockingOverlay, {
        data: {
          funnelKey,
          funnelRedirectPath: funnelRedirectPath.split("?")[0]
        }
      });
    }
  }, [show, autoOpenEnabled, funnelRedirectPath]);

  if (!show) {
    return null;
  }

  return (
    <StyledOverlay className={clsx("show")}>
      <WithBackground variant="animate-primary">
        {!autoOpenEnabled && (
          <FlexCenter>
            <HeadingSection>
              <h3 className="as-h4-large">
                <Translate msg={"complete_signup_title"} />
              </h3>
              <div style={{ height: "var(--space-md)" }} />
              <p className="m0 color-c-80">
                <Translate msg={"complete_signup_subtitle"} />
              </p>
            </HeadingSection>

            <div style={{ height: "var(--space-lg)" }} />

            <div className="center">
              <Button theme="charcoal" center onPress={handleContinueSignup}>
                {renderSignupButtonText}
              </Button>
              <div style={{ height: "var(--space-sm)" }} />
              <Button
                theme="transparent"
                hideArrow
                center
                onPress={handleLogout}
              >
                <Translate msg="button.backToLogin" />
              </Button>
            </div>
          </FlexCenter>
        )}
      </WithBackground>
    </StyledOverlay>
  );
};

export default UnfinishedSignupOverlay;
